import { useEffect, useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, Download, BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  getIncomeReport, IncomeReport as IncomeReportT, INCOME_TYPE_LABEL, INCOME_METHOD_LABEL,
} from '../api';
import { Skeleton } from '../../../components/Skeleton';
import { readCache, writeCache } from '../../../services/localCache';
import CountUp from '../../../components/CountUp';
import EmptyState from '../../../components/EmptyState';
import { SelectorVista, VistaPeriodo } from './PeriodoSelector';

function money(n: number | string) {
  return '$' + Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 });
}
const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const METHOD_COLOR: Record<string, string> = {
  cash: '#10b981', creditcard: '#3b82f6', card: '#3b82f6',
  wire: '#f59e0b', voucher: '#8b5cf6', other: '#94a3b8',
};

/** Dona SVG sin librería (r=15.915 → circunferencia=100). */
function Donut({ segments }: { segments: { color: string; pct: number }[] }) {
  let offset = 0;
  return (
    <svg viewBox="0 0 42 42" className="h-32 w-32 shrink-0 -rotate-90">
      <circle cx="21" cy="21" r="15.915" fill="none" className="stroke-surface-2" strokeWidth="5" />
      {segments.map((s, i) => {
        const el = (
          <circle key={i} cx="21" cy="21" r="15.915" fill="none" stroke={s.color} strokeWidth="5"
            strokeDasharray={`${s.pct} ${100 - s.pct}`} strokeDashoffset={-offset} />
        );
        offset += s.pct;
        return el;
      })}
    </svg>
  );
}

// Mes | Trimestre | Semestre (Mabel 23-oct-2026): agrupa los 12 meses del año en trimestres o semestres de calendario.
// `inicio` (24-oct-2026, «¿puedo recorrer los trimestres, por ejemplo feb–abril?»): mes en que arranca el primer grupo
// completo (0 = ene, calendario). Los meses que quedan antes o después en el año salen como grupo «(parcial)».
type Grupo = { key: string; label: string; total: number; n: number; byType: Record<string, number> };
function agrupar(r: IncomeReportT, vista: VistaPeriodo, inicio = 0): Grupo[] {
  const tam = vista === 'trimestre' ? 3 : vista === 'semestre' ? 6 : 1;
  const ini = tam === 1 ? 0 : inicio % tam;
  const cortes: [number, number][] = [];   // [desde, hasta) en índice de mes 0-11
  if (ini > 0) cortes.push([0, ini]);
  for (let i = ini; i < 12; i += tam) cortes.push([i, Math.min(12, i + tam)]);
  return cortes.map(([a, b], j) => {
    const ms = r.byMonth.filter((m) => m.month > a && m.month <= b);
    const byType: Record<string, number> = {};
    for (const m of ms) for (const [k, v] of Object.entries(m.byType || {})) byType[k] = (byType[k] || 0) + Number(v || 0);
    const tramo = b - a === 1 ? MONTHS[a] : `${MONTHS[a]}–${MONTHS[b - 1]}`;
    const label = tam === 1 ? MONTHS[a]
      : b - a < tam ? `${tramo} (parcial)`
      : ini === 0 ? (tam === 3 ? `T${a / 3 + 1} · ${tramo}` : `${a === 0 ? '1er' : '2º'} sem. · ${tramo}`)
      : tramo;
    return { key: `${a}-${j}`, label, total: ms.reduce((x, m) => x + m.total, 0), n: ms.reduce((x, m) => x + m.n, 0), byType };
  });
}

function exportCsv(r: IncomeReportT, vista: VistaPeriodo, inicio = 0) {
  const types = r.byType.map((t) => t.type);
  const header = [vista === 'trimestre' ? 'Trimestre' : vista === 'semestre' ? 'Semestre' : 'Mes', ...types.map((t) => INCOME_TYPE_LABEL[t] || t), 'Total', 'Pagos'];
  const lines = [header.join(',')];
  for (const g of agrupar(r, vista, inicio)) {
    lines.push([g.label, ...types.map((t) => Math.round(g.byType[t] || 0)), Math.round(g.total), g.n].join(','));
  }
  lines.push(['TOTAL', ...types.map((t) => Math.round(Number(r.byType.find((x) => x.type === t)?.total || 0))), Math.round(r.total), r.count].join(','));
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `ingresos-letsmove-${r.year}${vista === 'mes' ? '' : `-por-${vista}${inicio % (vista === 'trimestre' ? 3 : 6) ? `-desde-${MONTHS[inicio].toLowerCase()}` : ''}`}.csv`; a.click();
  URL.revokeObjectURL(url);
}

export default function IncomeReport() {
  // Pinta al instante lo último conocido por año (caché local) y refresca por detrás.
  const [data, setData] = useState<IncomeReportT | null>(() => readCache<IncomeReportT>('rep-income:actual') ?? null);
  const [year, setYear] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(() => !readCache('rep-income:actual'));
  const [vista, setVista] = useState<VistaPeriodo>('mes');
  const [inicio, setInicio] = useState(0);   // mes en que arranca el 1er trimestre/semestre (0 = ene)
  const tamG = vista === 'trimestre' ? 3 : vista === 'semestre' ? 6 : 1;

  useEffect(() => {
    const key = `rep-income:${year ?? 'actual'}`;
    const cached = readCache<IncomeReportT>(key);
    if (cached) { setData(cached); setLoading(false); } else setLoading(true);
    getIncomeReport(year).then((r) => { setData(r); writeCache(key, r); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [year]);

  const delta = useMemo(() => (data && data.prevTotal ? ((data.total - data.prevTotal) / data.prevTotal) * 100 : null), [data]);
  const grupos = useMemo(() => (data ? agrupar(data, vista, inicio) : []), [data, vista, inicio]);
  const maxMonth = useMemo(() => Math.max(1, ...grupos.map((g) => g.total)), [grupos]);
  const methodSum = data ? data.byMethod.reduce((a, b) => a + Number(b.total), 0) || 1 : 1;
  const typeMax = data ? Math.max(1, ...data.byType.map((t) => Number(t.total))) : 1;

  return (
    <div className="space-y-4">
      {/* Controles: año + CSV */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <SelectorVista vista={vista} onChange={setVista} />
        {vista !== 'mes' && (
          <div className="flex items-center gap-1 rounded-lg border border-line bg-surface p-0.5 text-sm" data-testid="ingresos-inicio" title={`Mover los ${vista}s un mes`}>
            <button onClick={() => setInicio((i) => (i % tamG + tamG - 1) % tamG)} aria-label="Empezar un mes antes" className="rounded-md p-1.5 text-content-2 hover:bg-surface-2"><ChevronLeft size={15} /></button>
            <span className="whitespace-nowrap px-1 text-content-2">Desde {MONTHS[inicio % tamG].toLowerCase()}</span>
            <button onClick={() => setInicio((i) => (i % tamG + 1) % tamG)} aria-label="Empezar un mes después" className="rounded-md p-1.5 text-content-2 hover:bg-surface-2"><ChevronRight size={15} /></button>
          </div>
        )}
        {data && (
          <div className="flex rounded-lg border border-line bg-surface p-0.5">
            {data.years.map((y) => (
              <button key={y} onClick={() => setYear(y)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                  y === data.year ? 'bg-accent text-on-accent' : 'text-content-2 hover:bg-surface-2'}`}>
                {y}
              </button>
            ))}
          </div>
        )}
        {data && data.count > 0 && (
          <button onClick={() => exportCsv(data, vista, inicio % tamG)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 hover:bg-surface-2">
            <Download size={15} /> CSV
          </button>
        )}
      </div>

      {loading || !data ? (
        <Skeleton className="h-96 w-full rounded-2xl" />
      ) : data.count === 0 ? (
        <EmptyState icon={BarChart3} title="Sin ingresos en este año" subtitle="Elige otro año con datos." />
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Total {data.year}</div>
              <div className="mt-1 font-display text-2xl font-bold text-content"><CountUp value={data.total} format={money} /></div>
              {delta !== null && (
                <div className={`mt-1 inline-flex items-center gap-1 text-xs font-medium ${
                  delta >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                  {delta >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                  {delta >= 0 ? '+' : ''}{delta.toFixed(0)}% vs {data.prevYear}
                </div>
              )}
            </div>
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Pagos</div>
              <div className="mt-1 font-display text-2xl font-bold text-content"><CountUp value={data.count} /></div>
              <div className="mt-1 text-xs text-content-3">{data.year}</div>
            </div>
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Promedio / mes</div>
              <div className="mt-1 font-display text-2xl font-bold text-content">
                <CountUp value={Math.round(data.total / (data.byMonth.filter((m) => m.total > 0).length || 1))} format={money} />
              </div>
              <div className="mt-1 text-xs text-content-3">meses con ingresos</div>
            </div>
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Ticket promedio</div>
              <div className="mt-1 font-display text-2xl font-bold text-content">
                <CountUp value={Math.round(data.total / (data.count || 1))} format={money} />
              </div>
              <div className="mt-1 text-xs text-content-3">por pago</div>
            </div>
          </div>

          {/* Barras por mes */}
          <div className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
            <div className="mb-4 text-sm font-medium text-content-2">Ingresos por {vista} — {data.year}</div>
            <div className="flex h-52 items-end gap-1.5 sm:gap-2">
              {grupos.map((m) => (
                <div key={m.key} className="group flex flex-1 flex-col items-center justify-end gap-1">
                  <div className="relative w-full">
                    {m.total > 0 && (
                      <div className="pointer-events-none absolute -top-7 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-md bg-content px-2 py-1 text-[11px] font-medium text-canvas opacity-0 shadow transition group-hover:opacity-100">
                        {money(m.total)}
                      </div>
                    )}
                    <div className="w-full rounded-t bg-accent transition group-hover:opacity-80"
                      style={{ height: `${Math.max(m.total > 0 ? 6 : 0, (m.total / maxMonth) * 180)}px` }} />
                  </div>
                  <span className="text-center text-[10px] text-content-3">{m.label}</span>
                  {vista !== 'mes' && <span className="text-xs font-semibold tabular-nums text-content">{money(m.total)}</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Por tipo */}
            <div className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
              <div className="mb-4 text-sm font-medium text-content-2">Por tipo de ingreso</div>
              <div className="space-y-3">
                {data.byType.map((t) => {
                  const v = Number(t.total);
                  return (
                    <div key={t.type}>
                      <div className="mb-1 flex items-center justify-between text-sm">
                        <span className="text-content-2">{INCOME_TYPE_LABEL[t.type] || t.type}</span>
                        <span className="font-medium text-content">{money(v)}
                          <span className="ml-1.5 text-xs text-content-3">{Math.round((v / data.total) * 100)}%</span>
                        </span>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-2">
                        <div className="h-full rounded-full bg-brand-blue" style={{ width: `${(v / typeMax) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Por método */}
            <div className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
              <div className="mb-4 text-sm font-medium text-content-2">Por método de pago</div>
              <div className="flex flex-col items-center gap-5 sm:flex-row">
                <Donut segments={data.byMethod.map((x) => ({
                  color: METHOD_COLOR[x.method] || '#94a3b8', pct: (Number(x.total) / methodSum) * 100,
                }))} />
                <div className="space-y-1.5">
                  {data.byMethod.map((x) => (
                    <div key={x.method} className="flex items-center gap-2 text-sm">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: METHOD_COLOR[x.method] || '#94a3b8' }} />
                      <span className="w-24 text-content-2">{INCOME_METHOD_LABEL[x.method] || x.method}</span>
                      <span className="font-medium text-content">{money(x.total)}</span>
                      <span className="text-content-3">{Math.round((Number(x.total) / methodSum) * 100)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
